import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { ArchiveRunView, RetentionDueSort, RetentionOverview, RetentionSettingsInput } from '@nest-recruit/shared';
import { apiGet, apiPost, apiPut, type ApiError } from '../../../lib/api-client';

/** S30 — the retention page. The realtime hint `['retention']` refreshes every page of it. */
export const RETENTION_KEY = ['retention'] as const;
const HTTP_CONFLICT = 409;

/** Which page of the due table, in which order (admin redesign 2026-11-02: paged on the server). */
export interface RetentionDueView {
  readonly page: number;
  readonly sort: RetentionDueSort;
  readonly dir: 'asc' | 'desc';
}

export function useRetention(isEnabled: boolean, view: RetentionDueView): UseQueryResult<RetentionOverview, ApiError> {
  return useQuery({
    queryKey: [...RETENTION_KEY, view],
    queryFn: () => apiGet<RetentionOverview>('/admin/retention', { query: { page: view.page, sort: view.sort, dir: view.dir } }),
    enabled: isEnabled,
    // Another page or order swaps the rows in place (§5a rule 2); the cards above never flinch.
    placeholderData: (previous) => previous,
  });
}

type Snapshot = readonly (readonly [readonly unknown[], RetentionOverview | undefined])[];

/**
 * The page shows the new settings at once, on every cached page; a refusal puts the old ones back
 * (§5a rule 1). The new version lands on every page before the refetch, so a second save never sends
 * a stale one.
 */
export function useSaveRetentionSettings(): UseMutationResult<RetentionOverview, ApiError, RetentionSettingsInput, { readonly previous: Snapshot }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) => apiPut<RetentionOverview>('/admin/retention/settings', input),
    onMutate: async (input) => {
      await client.cancelQueries({ queryKey: RETENTION_KEY });
      const previous = client.getQueriesData<RetentionOverview>({ queryKey: RETENTION_KEY });
      client.setQueriesData<RetentionOverview>({ queryKey: RETENTION_KEY }, (current) =>
        current ? { ...current, settings: { ...current.settings, periodDays: input.periodDays, mode: input.mode } } : current,
      );
      return { previous };
    },
    onError: (error, _input, context) => {
      for (const [key, data] of context?.previous ?? []) client.setQueryData(key, data);
      // Review W7: refused as stale — someone else saved. Read theirs, so the page can say so and offer to save on top.
      if (error.status === HTTP_CONFLICT) void client.invalidateQueries({ queryKey: RETENTION_KEY });
    },
    onSuccess: async (overview) => {
      client.setQueriesData<RetentionOverview>({ queryKey: RETENTION_KEY }, (current) =>
        current ? { ...current, settings: overview.settings, dryRunsCompleted: overview.dryRunsCompleted } : current,
      );
      // The period and the mode change who is due and what their status says.
      await client.invalidateQueries({ queryKey: RETENTION_KEY });
    },
  });
}

export function useRunArchive(): UseMutationResult<ArchiveRunView, ApiError, void> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<ArchiveRunView>('/admin/retention/archives'),
    onSettled: () => client.invalidateQueries({ queryKey: RETENTION_KEY }),
  });
}

export function archiveFileHref(archiveId: string, kind: 'zip' | 'workbook'): string {
  return `/api/v1/admin/retention/archives/${archiveId}/${kind}`;
}

/** "2026-10" (the server's month) as "October 2026". */
export function archiveMonthName(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(year, monthNumber - 1, 1)));
}
